import Papa from 'papaparse'
import type {
	ResourceSchema,
	ResourceSchemaColumn
} from '@semanticedge/persistence-service-noheaders-client/src'
import type { ReferenceTableRow } from '@repo/commons/models'
import {
	CsvDelimiter,
	CsvEncoding,
	createCsvValidator,
	downloadContent,
	type FileValidationResult,
	type ValidationError
} from '@repo/commons/utils'
import { columnTypeValidator } from './referenceTableRowValidation.js'
import { serializeManualColumnValue } from './resourceSchemaColumnInput.js'
import { getPrimaryKeyColumns, toOrderedColumnNames } from './resourceSchemaColumns.js'

const validatePrimaryKeyUniqueness = (
	rows: ReferenceTableRow[],
	primaryKeyColumns: string[]
): ValidationError[] => {
	const errors: string[] = []

	primaryKeyColumns.forEach((columnName) => {
		const firstRowByValue = new Map<string, number>()

		rows.forEach((row, index) => {
			const value = String(row[columnName] ?? '')
			const firstRow = firstRowByValue.get(value)

			if (firstRow !== undefined) {
				errors.push(
					`Row ${index + 1}: Duplicate primary key "${columnName}" — already used in row ${firstRow + 1}`
				)

				return
			}

			firstRowByValue.set(value, index)
		})
	})

	return errors.length > 0 ? [{ group: 'Primary Key Uniqueness', errors }] : []
}

const buildReferenceTableCsvSchema = (schema: ResourceSchema) => {
	const columns: Record<string, ResourceSchemaColumn> = schema.columns ?? {}
	const columnNames = Object.keys(columns)
	const primaryKeyColumns = getPrimaryKeyColumns(columns)

	const isRequired = (name: string): boolean =>
		columns[name]?.nullable === false || primaryKeyColumns.includes(name)

	const required = columnNames.filter(isRequired)

	const optional = columnNames.filter((name) => !isRequired(name))

	const validators = Object.fromEntries(
		columnNames.map((name) => [name, columnTypeValidator(columns[name]?.type)])
	)

	return {
		required,
		optional,
		validators
	}
}

const coerceReferenceTableRows = (
	rows: ReferenceTableRow[],
	schema: ResourceSchema
): ReferenceTableRow[] => {
	const columns: Record<string, ResourceSchemaColumn> = schema.columns ?? {}

	return rows.map((row) => {
		const coerced: ReferenceTableRow = {}

		Object.entries(columns).forEach(([name, column]) => {
			const raw = row[name]
			if (raw == null || raw === '') {
				coerced[name] = raw ?? null

				return
			}

			coerced[name] = serializeManualColumnValue(column.type, raw)
		})

		return coerced
	})
}

const detectCsvEncoding = async (file: File): Promise<CsvEncoding> => {
	const bytes = await file.arrayBuffer()

	try {
		new TextDecoder('utf-8', { fatal: true }).decode(bytes)

		return CsvEncoding.UTF8
	} catch {
		return CsvEncoding.ISO8859_1
	}
}

export const validateReferenceTableCsvFile = async (
	file: File,
	schema: ResourceSchema
): Promise<FileValidationResult<ReferenceTableRow>> => {
	const csvSchema = buildReferenceTableCsvSchema(schema)
	const validator = createCsvValidator<ReferenceTableRow>(csvSchema, {
		delimiter: CsvDelimiter.SEMICOLON,
		encoding: await detectCsvEncoding(file),
		headerTransformMode: 'none',
		caseInsensitiveHeaders: false
	})

	const result = await validator.validateFile(file)

	if (!result.data?.length) return result

	const data = coerceReferenceTableRows(result.data, schema)
	const primaryKeyErrors = validatePrimaryKeyUniqueness(data, getPrimaryKeyColumns(schema.columns))

	return { ...result, data, errors: [...result.errors, ...primaryKeyErrors] }
}

const getReferenceTableCsvColumnOrder = (schema?: ResourceSchema): string[] =>
	toOrderedColumnNames(schema?.columns, schema?.columnOrder)

export const downloadReferenceTableCsv = (
	rows: readonly ReferenceTableRow[],
	fileName: string,
	schema?: ResourceSchema
): void => {
	const columnOrder = getReferenceTableCsvColumnOrder(schema)

	const exportRows = rows.map((row) =>
		Object.fromEntries(columnOrder.map((columnName) => [columnName, row[columnName] ?? '']))
	)

	const csv = Papa.unparse(exportRows, {
		delimiter: CsvDelimiter.SEMICOLON,
		columns: columnOrder.length > 0 ? columnOrder : undefined
	})

	downloadContent(
		csv,
		fileName.endsWith('.csv') ? fileName : `${fileName}.csv`,
		'text/csv;charset=utf-8;'
	)
}
